import React from "react";
import { motion } from "framer-motion";
import MainLayout from "../layouts/MainLayout";
import Hero from "../components/common/Hero";
import { Separator } from "../components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FadeInUp, StaggerContainer, StaggerItem } from "../components/animations/AnimationComponents";
import { PageTransition } from "../components/animations/SpecialEffects";
import { GradientOrb } from "../components/animations/BackgroundEffects";
import placeholder from "../assets/images/placeholder.webp";
import { useI18n } from "../hooks/useI18n";
import { useInstitutionalOrganization } from "../hooks/useInstitutionalOrganization";

const About: React.FC = () => {
  const { text } = useI18n();
  const institutional = useInstitutionalOrganization();

  if (!institutional) return null;

  const cardData = [
    { title: text.about.history, content: institutional.history, icon: "📚" },
    { title: text.about.mission, content: institutional.mission, icon: "🎯" },
    { title: text.about.vision, content: institutional.vision, icon: "🌟" },
  ];

  return (
    <MainLayout>
      <PageTransition>
        <Hero title={text.about.heroTitle} subtitle={institutional.slogan} backgroundImage={placeholder} height="medium" />

        <section className="relative overflow-hidden bg-background py-16">
          <GradientOrb className="absolute right-10 top-20 -z-10" size="lg" color="gray" />
          <GradientOrb className="absolute bottom-20 left-10 -z-10" size="md" color="gray" />

          <div className="container mx-auto max-w-4xl text-center">
            <FadeInUp>
              <h2 className="gradient-text mb-4 text-3xl font-semibold">{institutional.name}</h2>
            </FadeInUp>

            <FadeInUp delay={0.2}>
              <p className="mx-auto mb-8 max-w-3xl text-lg leading-relaxed text-muted-foreground">{institutional.description}</p>
            </FadeInUp>

            <FadeInUp delay={0.4}>
              <Separator className="mb-12" />
            </FadeInUp>

            <StaggerContainer className="grid gap-8 text-left">
              {cardData.map((item) => (
                <StaggerItem key={item.title}>
                  <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                    <Card className="group">
                      <motion.div whileHover={{ backgroundColor: "rgba(49, 95, 69, 0.025)" }} transition={{ duration: 0.2 }}>
                        <CardHeader className="bg-surface-alt">
                          <CardTitle className="flex items-center gap-3 text-left">
                            <motion.span whileHover={{ scale: 1.2, rotate: 10 }} transition={{ duration: 0.3 }} className="text-2xl">
                              {item.icon}
                            </motion.span>
                            <span className="text-foreground">{item.title}</span>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6">
                          <motion.p
                            className="leading-relaxed text-muted-foreground"
                            initial={{ opacity: 0.8 }}
                            whileHover={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                          >
                            {item.content}
                          </motion.p>
                        </CardContent>
                      </motion.div>
                    </Card>
                  </motion.div>
                </StaggerItem>
              ))}
            </StaggerContainer>

            <FadeInUp delay={0.8} className="mt-16">
              <motion.div
                whileHover={{ y: -2 }}
                transition={{ duration: 0.2 }}
                className="rounded-ui-lg border border-nature/15 bg-surface-alt p-8 text-foreground shadow-ui-card"
              >
                <h3 className="mb-4 text-2xl font-bold">{text.about.communityTitle}</h3>
                <p className="mb-6 leading-relaxed text-muted-foreground">{text.about.communityBody}</p>
                <div className="flex flex-col gap-4 text-sm sm:flex-row">
                  <div className="flex items-center gap-2">
                    <span>📍</span>
                    <span>{institutional.contactInfo.address}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>📘</span>
                    <a
                      href={institutional.socialMedia.facebook}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-primary transition-colors hover:text-primary-dark"
                    >
                      {text.about.followFacebook}
                    </a>
                  </div>
                </div>
              </motion.div>
            </FadeInUp>
          </div>
        </section>
      </PageTransition>
    </MainLayout>
  );
};

export default About;
